/**
 * <product-price>
 * PDP price display.
 *
 * - Variant switching: update(variant) swaps in the selected variant's price
 *   HTML and refreshes the "4 payments of…" installment line.
 * - Takes the same JS variant object as <product-gallery>/<product-description>
 *   so all PDP components share one call signature. The localized price markup
 *   itself is not in the variant JSON (it's rendered server-side), so it is
 *   read from the matching hidden <option>'s data-price, looked up by id.
 * - Public API:
 *     update(variant)  // variant object from product-info-json's variants[]
 */

// Installment (Klarna/Afterpay) only kicks in above this price, mirroring the
// server-side threshold in the original Vue method.
const INSTALLMENT_MIN_PRICE = 35;

class ProductPrice extends HTMLElement {
  connectedCallback() {
    // The element itself is the price container (class="price" id="price-container").
    this.container = this;
    this._variantChangedHandler = (event) => {
      const variant = event?.detail?.variant;
      if (variant) this.update(variant);
    };
    window.addEventListener("enroute:variant-changed", this._variantChangedHandler);
  }

  disconnectedCallback() {
    window.removeEventListener("enroute:variant-changed", this._variantChangedHandler);
  }

  /**
   * Point the price at the selected variant. Driven by the variant switcher's
   * enroute:variant-changed event; also callable directly.
   *
   * @param {object} variant - variant object (has id, price in cents)
   */
  update(variant) {
    if (!variant) return;
    if (this.container) {
      // The localized price HTML lives on the hidden variant <option>, keyed by id.
      const optionEl = document.querySelector(`#id option[value="${variant.id}"]`);
      if (optionEl) this.container.innerHTML = optionEl.dataset.price;
    }
    this.updateInstallmentAmount(variant);
  }

  // Installment info lives in a separate block (product-installment-info.liquid),
  // so it is addressed by its global ids just like the original Vue code.
  updateInstallmentAmount(variant) {
    try {
      const installmentInfo = document.querySelector("#installment-info");
      if (!installmentInfo) return;

      // variant.price is in cents.
      const price = Number(variant.price) / 100;

      // only show installment info if price is greater than the threshold
      if (isNaN(price) || price < INSTALLMENT_MIN_PRICE) {
        installmentInfo.style.display = "none";
        return;
      }

      const installmentAmount = document.querySelector("#installment-amount");
      if (!installmentAmount) return;
      installmentAmount.innerHTML = Math.round((Number(price) / 4) * 100) / 100;
      installmentInfo.style.display = "block";
    } catch (e) {
      console.log(e);
    }
  }
}

if (!window.customElements.get("product-price")) {
  window.customElements.define("product-price", ProductPrice);
}
